import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConnectionBanner() {
  const [disconnected, setDisconnected] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    // Axios response interceptor for 502/503/504
    const interceptor = axios.interceptors.response.use(
      (response) => {
        // Connection recovered
        if (disconnected) setDisconnected(false);
        return response;
      },
      async (error) => {
        const status = error.response?.status;
        const isServerError = status === 502 || status === 503 || status === 504;
        const isNetworkError = !error.response && error.code !== 'ERR_CANCELED';

        // Show the disconnected banner regardless so the user knows the
        // connection is unstable.
        if (isServerError || isNetworkError) {
          setDisconnected(true);
        }

        // CRITICAL: only retry IDEMPOTENT methods. POSTing twice creates
        // two server records — that caused field workers to see 9-11
        // duplicate Locate / Soft Dig / PreCon drafts per submit when
        // their LTE dropped the response packet mid-flight. PUT is
        // idempotent in our API (full-payload replace), GET/HEAD/OPTIONS
        // are idempotent by HTTP spec. DELETE is idempotent enough.
        const method = (error.config?.method || 'get').toLowerCase();
        const isIdempotent = ['get', 'head', 'options', 'put', 'delete'].includes(method);

        if ((isServerError || isNetworkError) && isIdempotent) {
          // Auto-retry idempotent requests up to 3 times with backoff
          const config = error.config;
          if (!config._retryCount) config._retryCount = 0;

          if (config._retryCount < 3) {
            config._retryCount += 1;
            const delay = config._retryCount * 2000;
            await new Promise((r) => setTimeout(r, delay));
            return axios(config);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [disconnected]);

  // Health check polling when disconnected
  useEffect(() => {
    if (!disconnected) return;

    const poll = setInterval(async () => {
      try {
        setRetrying(true);
        await axios.get(`${API}/health`, { timeout: 5000, _retryCount: 99 });
        setDisconnected(false);
        setRetrying(false);
      } catch {
        setRetrying(false);
      }
    }, 8000);

    return () => clearInterval(poll);
  }, [disconnected]);

  if (!disconnected) return null;

  return (
    <div
      data-testid="connection-banner"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 99999,
        background: '#dc2626',
        color: '#fff',
        textAlign: 'center',
        padding: '10px 16px',
        fontSize: '14px',
        fontFamily: 'Inter, sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
      }}
    >
      {retrying ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      )}
      Connection lost — reconnecting automatically...
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
